import { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useTF911LoggedInUser from '@rsos/hooks/talkjs/useTF911LoggedInUser';
import { setConversationUnread } from '@rsos/reduxDataStore/src/features/conversations/conversationsSlice';
import {
  useGetConversationID,
  useHasUnreadConversationMessages,
  useIsConversationEnded,
} from '@rsos/reduxDataStore/src/features/conversations/utils';
import { StateWithEmergencyCalls } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { createTalkSession, createTalkUser } from '@rsos/utils/talkJS';
import useTalkJSTheme from '../../../../hooks/useTalkJSTheme';
import { ChatContainer } from './ConversationsChatbox.styles';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TalkJS = any; // TODO: Either add Talkjs Package or make a file for all talkjs typings

interface ConversationsChatProps {
  isChatBoxVisible: boolean;
}

const ConversationsChat = ({ isChatBoxVisible }: ConversationsChatProps) => {
  const callerID = useSelector(
    (state: StateWithEmergencyCalls) => state.emergencyCalls.selectedCallerID,
  );
  const dispatch = useDispatch();
  const chatRef = useRef<HTMLDivElement>(null);
  const [chatSession, setChatSession] = useState<TalkJS>(null);
  const [chatbox, setChatbox] = useState<TalkJS>(null);
  const chatTheme = useTalkJSTheme(true);
  const conversationID = useGetConversationID(callerID);
  const isConversationEnded = useIsConversationEnded(callerID);
  const loggedInUser = useTF911LoggedInUser();
  const hasUnreadConversationMessages =
    useHasUnreadConversationMessages(callerID);

  // Mount the TalkJS session and chat container
  useEffect(() => {
    let session, chat;
    try {
      (async () => {
        session = await createTalkSession(loggedInUser);

        chat = session.createChatbox({
          theme: chatTheme,
          showChatHeader: false,
          messageField: {
            visible: true,
            placeholder: 'Type Message',
          },
        });

        chat.mount(chatRef.current);

        setChatSession(session);

        setChatbox(chat);
      })();
    } catch (error) {
      if (session) {
        setChatbox(null);
      }
    }
  }, [chatTheme, loggedInUser]);

  // Set Conversation
  useEffect(() => {
    if (chatbox && chatSession && conversationID) {
      const conversation = chatSession.getOrCreateConversation(conversationID);

      conversation.setParticipant(createTalkUser(loggedInUser));
      chatbox.select(conversation);

      chatbox.messageField.setVisible(!isConversationEnded);
    }
  }, [
    chatSession,
    chatbox,
    conversationID,
    isConversationEnded,
    loggedInUser,
    callerID,
  ]);

  // Mark chat as read
  useEffect(() => {
    if (hasUnreadConversationMessages) {
      dispatch(setConversationUnread({ callerID, isUnread: false }));
    }
  }, [hasUnreadConversationMessages, callerID, dispatch]);

  return (
    <>
      <ChatContainer
        ref={chatRef}
        display={isChatBoxVisible ? 'flex' : 'none'}
      />
    </>
  );
};

export default ConversationsChat;
